// OVERLAY-LABEL-PLACEMENT-1 (2026-10-02): a hotspot label pill drawn at the
// bot-served `label_anchor` instead of just above the box.
//
// Rendered by DiagramHotspotRectV2 ONLY when the hotspot has a validated
// anchor AND the page has a validated `label_pill`; every other hotspot keeps
// the original label rule, untouched.
//
// Coordinate spaces. The parent <svg> uses viewBox 0 0 100 100 with
// preserveAspectRatio="none", so 1 x-unit = 1 % of image WIDTH and 1 y-unit =
// 1 % of image HEIGHT — a non-uniform space. The pill size is served in percent
// of image WIDTH on both axes, so the pill + text live in a group scaled by
// (1, imageAspect): inside it 1 unit = 1 % of image width in BOTH directions,
// the pill keeps the served proportions and the text is not stretched. The
// whole thing sits inside the zoom/pan transform, so it scales with the image.
//
// OVERLAY-LABEL-PLACEMENT-2 (2026-10-02): the leader is drawn whenever
// `anchor.leader` is true, on ANY side; end points come from the pure
// `computeLabelLeaderLine` (edge nearest the box -> box edge nearest the pill).
//
// OVERLAY-LABEL-HIDDEN-ONLY-1 (2026-10-04): the pill and the leader are hover /
// click targets of their hotspot. This component is rendered INSIDE the
// hotspot's own <g> (DiagramHotspotRectV2), which carries the box's mouse
// handlers, so events on the pill / leader bubble to exactly those handlers.
// The pill is already hit-testable (painted fill, and the viewer's overlay
// <g pointer-events="all"> is inherited). The 1 px leader gets a wider
// transparent hit stroke behind it; the visible line itself stays
// pointer-events none and unchanged.

import React from "react";
import {
  DiagramLabelAnchor,
  DiagramLabelPill,
} from "../../../../shared/chatBot/models/chatBotModels";
import {
  computeLabelLeaderLine,
  computeLabelPillFontSize,
  computeLabelPillWidthPct,
} from "../../utils/diagramLabelAnchor";

/**
 * On-screen width (px) of the leader's invisible hit stroke. Wide enough to
 * hover a 1 px line comfortably, narrow enough to take only a thin strip of
 * any box the leader crosses beneath it (boxes drawn above keep winning).
 */
export const LEADER_HIT_WIDTH_PX = 8;

interface Props {
  anchor: DiagramLabelAnchor;
  pill: DiagramLabelPill;
  labelText: string;
  /** Rendered image width / height (> 0). */
  imageAspect: number;
  /** Hotspot colour — pill fill and leader stroke. */
  color: string;
  /** Label text colour. */
  textColor: string;
  /** The hotspot box, in viewBox units — the leader's end point. */
  boxX: number;
  boxY: number;
  boxW: number;
  boxH: number;
}

const DiagramAnchoredLabelV2: React.FC<Props> = ({
  anchor,
  pill,
  labelText,
  imageAspect,
  color,
  textColor,
  boxX,
  boxY,
  boxW,
  boxH,
}) => {
  const pillW = computeLabelPillWidthPct(labelText, pill);
  // Pill height converted to viewBox y-units (percent of image HEIGHT) — only
  // needed for the leader, which is drawn in the parent's non-uniform space.
  const pillHY = pill.h_pct_w * imageAspect;
  const leaderLine = anchor.leader
    ? computeLabelLeaderLine(
        anchor.side,
        { x: anchor.x_pct, y: anchor.y_pct, w: pillW, h: pillHY },
        { x: boxX, y: boxY, w: boxW, h: boxH }
      )
    : null;

  return (
    <>
      {/* HIDDEN-ONLY-1: the leader's hit area — same geometry, transparent,
          LEADER_HIT_WIDTH_PX wide on screen. pointer-events "stroke" hits on
          the stroke regardless of its paint; no fill hit. Behind the visible
          line, at this hotspot's own z-level, so boxes drawn later still win
          where they overlap it. */}
      {leaderLine && (
        <line
          x1={leaderLine.x1}
          y1={leaderLine.y1}
          x2={leaderLine.x2}
          y2={leaderLine.y2}
          stroke="transparent"
          strokeWidth={LEADER_HIT_WIDTH_PX}
          vectorEffect="non-scaling-stroke"
          pointerEvents="stroke"
          data-leader-hit="true"
        />
      )}

      {/* Leader (any side, when `leader` is true): 1 px from the pill's edge
          nearest the box to the box's edge nearest the pill.
          non-scaling-stroke keeps it 1 px despite the non-uniform viewBox.
          The visible line is not itself a hit target — the hit stroke above
          is. */}
      {leaderLine && (
        <line
          x1={leaderLine.x1}
          y1={leaderLine.y1}
          x2={leaderLine.x2}
          y2={leaderLine.y2}
          stroke={color}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
          pointerEvents="none"
        />
      )}

      <g
        transform={`translate(${anchor.x_pct} ${anchor.y_pct}) scale(1 ${imageAspect})`}
      >
        <rect
          x={0}
          y={0}
          width={pillW}
          height={pill.h_pct_w}
          fill={color}
          rx="0.3"
          ry="0.3"
        />
        <text
          x={pillW / 2}
          y={pill.h_pct_w / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill={textColor}
          fontSize={computeLabelPillFontSize(pill)}
          fontFamily="'JetBrains Mono', 'Consolas', monospace"
          fontWeight="700"
        >
          {labelText}
        </text>
      </g>
    </>
  );
};

export default DiagramAnchoredLabelV2;
